import { Flex, Icon, Skeleton, Text } from "@chakra-ui/react";
import {
  type ChakraStylesConfig,
  type GroupBase,
  type OptionBase,
  Select,
  type SelectComponentsConfig,
  chakraComponents,
} from "chakra-react-select";
import { useMemo } from "react";
import "react-calendar/dist/Calendar.css";
import { useSnapshot } from "valtio";

import {
  AiTemperature,
  type ContactMethod,
  type InputMaybe,
  type RecKind,
  useTemplateSelectorQuery,
} from "@/generated/graphql";

import { ErrorBoundaryWithFeedback } from "@/app/components/ErrorBoundaryWithFeedback";
import { authState } from "@/app/state/authState";
import { icons } from "@/app/theme";
import { contactMethodIcon } from "@/app/utils/contactMethodIcon";
import { reportErrorOr404 } from "@/app/utils/report";

interface SelectTemplate {
  id: string;
  name: string;
  contactMethod: ContactMethod;
  aiTemperature: AiTemperature;
  templateAuthor?: {
    id: string;
    name: string;
  } | null;
}

interface TemplateOption extends OptionBase {
  label: string;
  value: string;
  contactMethod: ContactMethod;
  aiTemperature: AiTemperature;
  templateAuthorId: string | null;
  templateAuthorName: string | null;
}

export function TemplateInput({
  value,
  onChange,
}: {
  value: {
    templateId: InputMaybe<string>;
    contactMethod?: RecKind;
  };
  onChange: (value: { templateId: InputMaybe<string>; contactMethod?: RecKind }) => void;
}) {
  const authSnap = useSnapshot(authState.auth);
  const userId = authSnap.backend.user?.id;

  const { data, loading, error } = useTemplateSelectorQuery();

  const templates = data?.recTemplates;

  const sortedTemplates = useMemo(() => {
    if (!templates) return [];

    return [...templates].sort((a, b) => {
      const dateComparison = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

      if (a.templateAuthor?.id === userId && b.templateAuthor?.id !== userId) {
        return -1;
      }
      if (a.templateAuthor?.id !== userId && b.templateAuthor?.id === userId) {
        return 1;
      }

      return dateComparison;
    });
  }, [templates, userId]);

  if (loading) return <Skeleton h="34px" />;

  if (error || !templates) {
    return reportErrorOr404({ error, type: "RecTemplateType" });
  }

  const templatesOptions = transformTemplatesToOptions(sortedTemplates);

  const chakraStyles: ChakraStylesConfig<TemplateOption, false, GroupBase<TemplateOption>> = {
    option: (provided) => ({
      ...provided,
      display: "flex",
      gap: "16px",
    }),
  };

  function transformTemplatesToOptions(templates: SelectTemplate[]): TemplateOption[] {
    return templates.map((template) => ({
      label: template.name,
      value: template.id.toString(),
      contactMethod: template.contactMethod,
      aiTemperature: template.aiTemperature,
      templateAuthorId: template.templateAuthor?.id || null,
      templateAuthorName: template.templateAuthor?.name || null,
    }));
  }

  const customComponents: SelectComponentsConfig<
    TemplateOption,
    false,
    GroupBase<TemplateOption>
  > = {
    Option: ({ ...props }) => (
      <chakraComponents.Option {...props}>
        <Icon
          as={contactMethodIcon(props.data.contactMethod)}
          color={props.isSelected ? "white" : "blue.600"}
        />
        <Flex overflow="hidden" alignItems="center">
          {props.data.aiTemperature == AiTemperature.Predictable && (
            <Icon
              color={props.isSelected ? "purple.100" : "purple.600"}
              mr="5px"
              as={icons.AI}
            ></Icon>
          )}
          <Text textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
            {props.label}
          </Text>
        </Flex>
        {props.data.templateAuthorId && (
          <Flex alignItems="center" gap={1} whiteSpace="nowrap" ml="auto">
            <Icon as={icons.autor} color={props.isSelected ? "white" : "blue.500"} />
            <Text color={props.isSelected ? "white" : "blue.500"}>
              {props.data.templateAuthorId == userId ? "You" : props.data.templateAuthorName}
            </Text>
          </Flex>
        )}
      </chakraComponents.Option>
    ),
    SingleValue: ({ ...props }) => (
      <chakraComponents.SingleValue {...props}>
        <Flex alignItems="center" gap="8px">
          <Icon as={contactMethodIcon(props.data.contactMethod)} color="blue.600" />
          <Flex overflow="hidden" alignItems="center">
            {props.data.aiTemperature == AiTemperature.Predictable && (
              <Icon color="purple.600" mr="5px" as={icons.AI}></Icon>
            )}
            <Text textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
              {props.data.label}
            </Text>
          </Flex>
        </Flex>
      </chakraComponents.SingleValue>
    ),
  };

  return (
    <ErrorBoundaryWithFeedback>
      <Select<TemplateOption, false, GroupBase<TemplateOption>>
        chakraStyles={chakraStyles}
        name="templateSelect"
        placeholder="Select a template"
        options={templatesOptions}
        components={customComponents}
        defaultValue={templatesOptions.find((option) => option.value === value.templateId)}
        onChange={(event) => {
          const selectedOption = event as TemplateOption;
          onChange({
            templateId: selectedOption.value,
            contactMethod: selectedOption.contactMethod as unknown as RecKind,
          });
        }}
      />
    </ErrorBoundaryWithFeedback>
  );
}
